/**
 * Minimal ADF node shape needed to count words. Kept structural (no
 * `@atlaskit/*` type dependency) so this stays a cheap, self-contained util;
 * the public entry accepts `unknown` and narrows to this.
 */
type AdfNode = {
	type?: string;
	text?: string;
	content?: unknown[];
};

/**
 * Upper bound on nodes visited per count. The Live Docs producer recomputes on
 * every debounced edit, so the walk must stay bounded on very large documents.
 * Generous enough that typical live docs are counted in full and exactly; only a
 * document exceeding the cap yields a lower-bound count (biased to the top of the
 * document), which is acceptable — the share threshold only cares whether "enough"
 * words were added.
 */
export const MAX_WORD_COUNT_NODES = 20_000;

const countMatches = (text: string): number => text.match(/\S+/g)?.length ?? 0;

const asNode = (value: unknown): AdfNode | null =>
	typeof value === 'object' && value !== null ? (value as AdfNode) : null;

/**
 * Cheap, node-capped word count over an ADF document. Unlike read-time's
 * `getWordCount`, this does no whole-document `JSON.stringify` and stops after
 * `maxNodes` nodes. Expand / nestedExpand subtrees are skipped to match the
 * word-count semantics the read-time byline uses. Accepts `unknown` so the
 * editor's `JSONDocNode` can be passed without a shared type dependency.
 */
export const countWordsCapped = (doc: unknown, maxNodes: number = MAX_WORD_COUNT_NODES): number => {
	const root = asNode(doc);
	if (!root) {
		return 0;
	}

	let words = 0;
	let visited = 0;
	// Iterative DFS; each node is enqueued and visited exactly once.
	const stack: AdfNode[] = [root];
	while (stack.length > 0) {
		const node = stack.pop() as AdfNode;
		visited += 1;
		if (visited > maxNodes) {
			break;
		}
		if (node.type === 'expand' || node.type === 'nestedExpand') {
			continue; // skip the whole expand subtree
		}
		if (node.type === 'text' && typeof node.text === 'string') {
			words += countMatches(node.text);
		}
		if (Array.isArray(node.content)) {
			// Push children in reverse so they pop left-to-right (pre-order): when the
			// node cap trips, the counted nodes stay biased to the top of the document.
			// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- single DFS; each ADF node is pushed and visited exactly once (O(total nodes)), not quadratic
			for (let i = node.content.length - 1; i >= 0; i--) {
				const childNode = asNode(node.content[i]);
				if (childNode) {
					stack.push(childNode);
				}
			}
		}
	}
	return words;
};
